import type { FC } from 'react';

import {
  type GaugeMetricData,
  type GaugeZone,
} from '@/warranty/domain/entities/gauge-metric';

import { tailwindColorMap } from '../tailwind.colors';

// ---------------------------------------------------------------------------
// helpers -------------------------------------------------------------------
// ---------------------------------------------------------------------------

type Status = 'ok' | 'warn' | 'neutral';

type StatusMeta = {
  label: string;
  pill: string; // Tailwind classes for the coloured pill
  text: string; // Tailwind classes for the text rows
};

const statusMeta: Record<Status, StatusMeta> = {
  ok: {
    label: 'Supplier Compliant',
    pill: 'bg-emerald-100 text-emerald-700',
    text: 'text-gray-800',
  },
  warn: {
    label: 'Supplier Non-Compliant — Claim Eligible',
    pill: 'bg-amber-100 text-amber-700',
    text: 'text-gray-800',
  },
  neutral: {
    label: 'N/A',
    pill: 'bg-gray-200 text-gray-600',
    text: 'text-gray-800',
  },
};

const getStatus = (
  prediction: number | undefined,
  zones: GaugeZone[],
): Status => {
  if (prediction === undefined) return 'neutral';
  const zone = zones.find((z) => prediction >= z.from && prediction <= z.to);
  if (!zone) return 'neutral';
  if (zone.color === tailwindColorMap.ok) return 'ok';
  if (zone.color === tailwindColorMap.warning) return 'warn';
  return 'neutral';
};

// ---------------------------------------------------------------------------
// component -----------------------------------------------------------------
// ---------------------------------------------------------------------------

type GaugeInfoCardProps = {
  metric: GaugeMetricData;
  /**
   * Where to render: above the gauge ("top") or below ("bottom").
   * Defaults to bottom.
   */
  placement?: 'top' | 'bottom';
  /** Forward any utility classes */
  className?: string;
};

export const GaugeInfoCard: FC<GaugeInfoCardProps> = ({
  metric,
  placement = 'bottom',
  className = '',
}) => {
  // Use yearToDate value as metric to define the warranty status
  const status: Status = getStatus(metric.yearToDate, metric.zones);
  const meta = statusMeta[status];

  const isActive = metric.state === 'active';

  const isPreliminaryFallback =
    metric.yearToDate === undefined && metric.id === 'SOH' && isActive;

  // Determine YTD display value (fallback to 100 for SOH when missing)
  const displayYtd =
    typeof metric.yearToDate === 'number'
      ? metric.yearToDate.toFixed(1) + '%'
      : isPreliminaryFallback
        ? '100%*'
        : 'N/A';

  const displayThreshold =
    typeof metric.zones.at(0)?.to === 'number'
      ? metric.zones[0].to.toFixed(1) + '%'
      : isPreliminaryFallback
        ? '100%*'
        : 'N/A';

  return (
    <div
      className={`flex flex-col items-center text-[13px] leading-snug ${
        placement === 'top' ? 'mb-2' : 'mt-2'
      } ${className}`}
    >
      {/* status pill */}
      <span
        className={`mb-1 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${meta.pill}`}
      >
        {meta.label}
      </span>

      {/* numbers with inline fallback hint */}
      <div className={`${meta.text} space-y-1 text-center`}>
        <div>
          Current (YTD):{' '}
          <span
            className={`font-semibold ${isPreliminaryFallback ? 'italic' : ''}`}
          >
            {displayYtd}
          </span>
        </div>

        {isPreliminaryFallback && (
          <div className="text-xs italic text-gray-500">
            * Preliminary assumption for SOH
          </div>
        )}

        {metric.futurePrediction !== undefined && (
          <div>
            Prediction:{' '}
            <span className="font-semibold">
              {metric.futurePrediction + '%'}
            </span>
            {metric.predictionWindowEnd && (
              <span className="ml-1 text-xs text-gray-500">
                (through {metric.predictionWindowEnd.toLocaleDateString()})
              </span>
            )}
          </div>
        )}
        <div>
          Threshold:{' '}
          <span
            className={`font-semibold ${isPreliminaryFallback ? 'italic' : ''}`}
          >
            {displayThreshold}
          </span>
        </div>
      </div>
    </div>
  );
};

export default GaugeInfoCard;
